// src/pages/PostPage.tsx
import React, { useState, useEffect, useCallback, useTransition } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { getPostDetails, getComments, getMoreComments } from "../api/redditApi";
import { RedditPost, RedditComment, RedditMoreComments } from "../types/reddit";
import PostContent from "../components/PostContent";
import CommentTree from "../components/CommentTree";
import axios from "axios";
import NsfwWarningModal from "../components/NsfwWarningModal";
import {
  BLOCK_NSFW,
  getSessionNsfwConsent,
  setSessionNsfwConsent,
  getGeminiApiKeySetting,
} from "../utils/settings";
import GeminiTldrCard from "../components/GeminiTldrCard";
import { generateTldr } from "../api/geminiApi"; // Import the generation function

// Helper: Format date like PaperMod: January 2, 2006
const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Helper: Format time difference
const timeAgo = (timestamp: number): string => {
  const now = Date.now();
  const secondsPast = Math.floor((now - timestamp * 1000) / 1000);
  if (secondsPast < 60) return `${secondsPast}s ago`;
  if (secondsPast < 3600) return `${Math.floor(secondsPast / 60)}m ago`;
  if (secondsPast <= 86400) return `${Math.floor(secondsPast / 3600)}h ago`;
  const days = Math.floor(secondsPast / 86400);
  return `${days}d ago`;
};

const PostPage: React.FC = () => {
  const { subredditName, postId } = useParams<{
    subredditName: string;
    postId: string;
  }>();
  const location = useLocation();

  // Post & Comment State
  const [post, setPost] = useState<RedditPost | null>(null);
  const [comments, setComments] = useState<
    (RedditComment | RedditMoreComments)[]
  >([]);
  const [isLoadingPost, setIsLoadingPost] = useState<boolean>(true);
  const [isLoadingComments, setIsLoadingComments] = useState<boolean>(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [loadingMoreId, setLoadingMoreId] = useState<string | null>(null);
  const [commentsFetchAttempted, setCommentsFetchAttempted] =
    useState<boolean>(false);

  // NSFW State
  const [showNsfwWarning, setShowNsfwWarning] = useState<boolean>(false);
  const [isNsfwAllowed, setIsNsfwAllowed] = useState<boolean>(false);

  // Gemini State
  const [geminiApiKey, setGeminiApiKey] = useState<string | null>(null);
  const [tldrContent, setTldrContent] = useState<string | null>(null);
  const [isTldrLoading, setIsTldrLoading] = useState<boolean>(false);
  const [tldrError, setTldrError] = useState<string | null>(null);
  const [isTldrCardVisible, setIsTldrCardVisible] = useState<boolean>(false); // Controls card visibility

  // useTransition for non-blocking comment rendering
  const [isCommentsPending, startCommentsTransition] = useTransition();

  // --- Effects for Scrolling ---
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [postId]);
  useEffect(() => {
    if (
      location.hash === "#comments" &&
      post &&
      !isLoadingPost &&
      !isCommentsPending &&
      !showNsfwWarning
    ) {
      const commentsElement = document.getElementById("comments-section");
      requestAnimationFrame(() => {
        commentsElement?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash, post, isLoadingPost, isCommentsPending, showNsfwWarning]);

  // --- Comment Loading Logic ---
  const replaceMoreComments = useCallback(
    (
      currentComments: (RedditComment | RedditMoreComments)[],
      moreIdToReplace: string,
      newComments: (RedditComment | RedditMoreComments)[]
    ): (RedditComment | RedditMoreComments)[] => {
      // Use a map for potentially better performance on deep trees, though flatMap is often fine
      const updatedComments = currentComments.map((comment) => {
        if (comment.kind === "more" && comment.data.id === moreIdToReplace) {
          // Found the 'more' object at this level, return the new comments instead
          return newComments; // This will be flattened by flatMap later if we use it, or handled by map returning an array
        } else if (
          comment.kind === "t1" &&
          comment.data.replies &&
          typeof comment.data.replies === "object" &&
          comment.data.replies.kind === "Listing" &&
          comment.data.replies.data?.children?.length
        ) {
          // Recursively update replies within this comment
          const updatedReplies = replaceMoreComments(
            comment.data.replies.data.children,
            moreIdToReplace,
            newComments
          );
          // Only create a new comment object if replies actually changed
          if (updatedReplies !== comment.data.replies.data.children) {
            return {
              ...comment,
              data: {
                ...comment.data,
                replies: {
                  ...comment.data.replies,
                  data: {
                    ...comment.data.replies.data,
                    children: updatedReplies,
                  },
                },
              },
            };
          }
        }
        // Return the original comment if no changes needed at this level or below
        return comment;
      });
      // Flatten the array in case map returned arrays (from replacing 'more')
      return updatedComments.flat();
    },
    []
  );

  const handleLoadMoreComments = useCallback(
    async (moreData: RedditMoreComments["data"]) => {
      if (!postId || loadingMoreId) return;
      setLoadingMoreId(moreData.id);
      setCommentsError(null); // Clear previous 'more' errors
      try {
        const newComments = await getMoreComments(postId, moreData.children);
        // Update the state using the recursive replacement function
        startCommentsTransition(() => {
          setComments((prevComments) =>
            replaceMoreComments(prevComments, moreData.id, newComments)
          );
        });
      } catch (err: any) {
        console.error("Failed load more comments:", err);
        // Display error specific to loading more, maybe near the button in future?
        // For now, setting a general comment error might be okay, but indicates a partial load failure.
        setCommentsError(`Failed to load more comments. ${err.message}`);
        // We don't remove the 'more' button on error, user can retry
      } finally {
        setLoadingMoreId(null); // Allow other 'more' buttons to be clicked
      }
    },
    [postId, loadingMoreId, replaceMoreComments]
  );

  // --- Fetch Initial Data (Post Details, NSFW check, Gemini Key) ---
  useEffect(() => {
    if (!postId) {
      setPostError("Post ID missing from URL.");
      setIsLoadingPost(false);
      return;
    }
    setGeminiApiKey(getGeminiApiKeySetting());
    const fetchPostData = async () => {
      console.log(`PostPage: Initiating fetch for post ${postId}`);
      setIsLoadingPost(true);
      setPostError(null);
      setPost(null);
      setComments([]);
      setCommentsError(null);
      setIsLoadingComments(false);
      setCommentsFetchAttempted(false);
      setLoadingMoreId(null);
      setShowNsfwWarning(false);
      setIsNsfwAllowed(false);
      // Reset TLDR state for new post
      setIsTldrCardVisible(false);
      setTldrContent(null);
      setTldrError(null);
      setIsTldrLoading(false);

      try {
        const postListing = await getPostDetails(postId, subredditName);
        if (postListing === null) {
          throw new Error("Post not found (404).");
        }
        if (
          postListing.kind !== "Listing" ||
          !postListing.data?.children ||
          postListing.data.children.length === 0 ||
          !postListing.data.children[0]?.data
        ) {
          throw new Error("Post data structure invalid after fetch.");
        }
        const fetchedPost = postListing.data.children[0].data as RedditPost;
        console.log(
          `PostPage: Successfully fetched and validated post details for ${postId}.`
        );
        if (fetchedPost.over_18) {
          if (BLOCK_NSFW) {
            throw new Error("NSFW content is blocked in settings.");
          }
          const consentGiven = getSessionNsfwConsent(`post_${postId}`);
          if (!consentGiven) {
            setShowNsfwWarning(true);
          } else {
            setIsNsfwAllowed(true);
            setPost(fetchedPost);
          }
        } else {
          setIsNsfwAllowed(true);
          setPost(fetchedPost);
        }
      } catch (err: any) {
        console.error(
          `PostPage: Error during fetchPostData for ${postId}:`,
          err
        );
        if (err.name !== "CanceledError" && !axios.isCancel(err)) {
          setPostError(`Failed to load post details. (${err.message})`);
        }
        setPost(null);
      } finally {
        console.log(
          `PostPage: Finished fetchPostData attempt for ${postId}. isLoadingPost: false.`
        );
        setIsLoadingPost(false);
      }
    };
    fetchPostData();
  }, [postId, subredditName]);

  // --- Fetch Comments Effect ---
  useEffect(() => {
    if (
      post &&
      postId &&
      !isLoadingComments &&
      !commentsFetchAttempted &&
      isNsfwAllowed &&
      !postError
    ) {
      const fetchCommentData = async () => {
        console.log(
          `PostPage: Initiating fetch for comments of post ${postId}`
        );
        setIsLoadingComments(true);
        setCommentsFetchAttempted(true);
        setCommentsError(null);
        try {
          const commentListing = await getComments(postId, subredditName);
          console.log(`PostPage: Received comments listing for ${postId}.`);
          startCommentsTransition(() => {
            if (commentListing?.data?.children) {
              setComments(
                commentListing.data.children as (
                  | RedditComment
                  | RedditMoreComments
                )[]
              );
            } else {
              setComments([]);
            }
          });
        } catch (err: any) {
          console.error(`PostPage: Failed fetch comments for ${postId}:`, err);
          if (err.name !== "CanceledError" && !axios.isCancel(err)) {
            startCommentsTransition(() => {
              setCommentsError(`Failed to load comments. (${err.message})`);
            });
          }
        } finally {
          // Ensure loading state is reset even if transition hasn't finished
          setIsLoadingComments(false);
        }
      };
      fetchCommentData();
    } else {
      if (!post && !isLoadingPost && !postError)
        console.log(
          `PostPage: Skipping comment fetch for ${postId} - post data is null.`
        );
      if (isLoadingComments)
        console.log(
          `PostPage: Skipping comment fetch for ${postId} - already loading comments.`
        );
      if (commentsFetchAttempted)
        console.log(
          `PostPage: Skipping comment fetch for ${postId} - comments already fetched/attempted.`
        );
      if (!isNsfwAllowed && !isLoadingPost && !postError)
        console.log(
          `PostPage: Skipping comment fetch for ${postId} - NSFW consent not granted or post not loaded yet.`
        );
      if (postError)
        console.log(
          `PostPage: Skipping comment fetch for ${postId} - post error exists: ${postError}`
        );
    }
  }, [
    post,
    postId,
    subredditName,
    isLoadingComments,
    commentsFetchAttempted,
    postError,
    isNsfwAllowed,
  ]);

  // --- NSFW Handlers ---
  const handleNsfwConsent = () => {
    if (!postId) return;
    console.log(`PostPage: NSFW consent granted for ${postId}.`);
    setSessionNsfwConsent(`post_${postId}`);
    setIsNsfwAllowed(true);
    setShowNsfwWarning(false);
    // Fetch post again if it wasn't set due to missing consent
    if (!post) {
      const fetchPostAgain = async () => {
        console.log(`PostPage: Refetching post ${postId} after NSFW consent.`);
        setIsLoadingPost(true);
        try {
          const postListing = await getPostDetails(postId, subredditName);
          if (postListing === null)
            throw new Error("Post not found (404) on refetch.");
          if (
            postListing.kind !== "Listing" ||
            !postListing.data?.children ||
            postListing.data.children.length === 0 ||
            !postListing.data.children[0]?.data
          ) {
            throw new Error("Post data structure invalid on refetch.");
          }
          setPost(postListing.data.children[0].data as RedditPost);
          setPostError(null);
        } catch (err: any) {
          console.error(
            `PostPage: Error refetching post ${postId} after consent:`,
            err
          );
          setPostError(`Failed to reload post after consent. (${err.message})`);
          setPost(null);
        } finally {
          setIsLoadingPost(false);
        }
      };
      fetchPostAgain();
    }
  };
  const handleNsfwDecline = () => {
    console.log(`PostPage: NSFW consent declined for ${postId}.`);
    setPostError("Viewing NSFW content declined.");
    setShowNsfwWarning(false);
    setPost(null);
  };

  // --- TL;DR Generation and Dismissal Handlers ---
  const handleGenerateTldr = useCallback(async () => {
    if (!geminiApiKey || !post || !commentsFetchAttempted) {
      setTldrError(
        "Cannot generate TL;DR. Missing API key, post data, or comments."
      );
      setIsTldrCardVisible(true); // Show card to display error
      return;
    }

    setIsTldrLoading(true);
    setTldrError(null);
    setTldrContent(null); // Clear previous content
    setIsTldrCardVisible(true); // Show loading state in card

    try {
      // Filter out 'more' objects before sending to Gemini
      const actualComments = comments.filter(
        (c) => c.kind === "t1"
      ) as RedditComment[];
      const content = await generateTldr(
        geminiApiKey,
        post.title,
        post.selftext,
        actualComments
      );
      setTldrContent(content);
    } catch (err: any) {
      setTldrError(
        err.message || "An unknown error occurred during generation."
      );
    } finally {
      setIsTldrLoading(false);
    }
  }, [geminiApiKey, post, comments, commentsFetchAttempted]);

  const handleDismissTldr = () => {
    setIsTldrCardVisible(false);
  };

  // --- Render Logic ---

  if (isLoadingPost) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  if (showNsfwWarning) {
    return (
      <NsfwWarningModal
        contentType="post"
        onConfirm={handleNsfwConsent}
        onDecline={handleNsfwDecline}
      />
    );
  }
  if (postError) {
    return (
      <div className="prose-wrapper my-6">
        <div
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm"
          role="alert"
        >
          <strong>Error: </strong> {postError}
          <p className="mt-2">
            <Link
              to="/"
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Go Home
            </Link>
          </p>
        </div>
      </div>
    );
  }
  if (!post) {
    console.error(
      `PostPage: Reached render stage with post=null, isLoadingPost=false, postError=null for ${postId}. This should not happen.`
    );
    return (
      <div className="prose-wrapper my-10 text-center text-gray-500 dark:text-gray-400">
        Post not found or could not be loaded.
      </div>
    );
  }

  // --- Post Loaded, Render Content ---
  const displayPost = post.crosspost_parent_list?.[0] ?? post;
  const redditPostUrl = `https://www.reddit.com${displayPost.permalink}`;
  const showCommentsLoading = isLoadingComments; // Simpler check now

  // Dynamic title class based on length
  const titleLength = displayPost.title.length;
  let titleClass = "text-3xl md:text-4xl"; // Default
  if (titleLength > 200) {
    titleClass = "text-xl md:text-2xl"; // Smallest for very long
  } else if (titleLength > 100) {
    titleClass = "text-2xl md:text-3xl"; // Medium for long
  }

  // Construct the correct subreddit link based on displayPost
  const subredditLink = `/r/${displayPost.subreddit}`;

  return (
    <div className="prose-wrapper space-y-12">
      <article>
        {/* Post Header */}
        <header className="mb-8">
          {/* Apply dynamic title class */}
          <h1
            className={`${titleClass} font-extrabold text-gray-900 dark:text-white !leading-tight !mb-3`}
          >
            {displayPost.title}
          </h1>
          {/* --- METADATA BAR --- */}
          <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span title={formatDate(displayPost.created_utc)}>
              {timeAgo(displayPost.created_utc)}
            </span>
            <span aria-hidden="true">•</span>
            <span>{displayPost.author}</span>
            <span aria-hidden="true">•</span>
            {/* Use Link for internal navigation */}
            <Link to={subredditLink} className="hover:underline">
              {displayPost.subreddit_name_prefixed}
            </Link>
            <span aria-hidden="true">•</span>
            <span title={`${displayPost.score} points`}>
              {displayPost.score} pts
            </span>
            <span aria-hidden="true">•</span>
            {/* Use a tag for external link */}
            <a
              href={redditPostUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline inline-flex items-center"
              title="View on Reddit"
            >
              reddit.com
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 ml-0.5 opacity-70"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </a>
            {/* --- TL;DR Trigger Link --- */}
            {geminiApiKey &&
              commentsFetchAttempted && ( // Show only if key exists and comments attempted
                <>
                  <span aria-hidden="true">•</span>
                  <button
                    onClick={handleGenerateTldr}
                    disabled={isTldrLoading} // Disable while loading
                    className={`hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500 rounded ${
                      isTldrLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    aria-label="Generate TL;DR summary"
                  >
                    TL;DR
                  </button>
                </>
              )}
          </div>
          {post.crosspost_parent_list &&
            post.crosspost_parent_list.length > 0 && (
              <div className="mt-3 text-xs italic text-gray-500 dark:text-gray-400 border-l-2 border-gray-300 dark:border-gray-600 pl-2">
                {/* Ensure original post data exists before accessing */}
                Crossposted from{" "}
                <Link
                  to={`/r/${post.subreddit}`}
                  className="font-medium hover:underline"
                >
                  {post.subreddit_name_prefixed}
                </Link>{" "}
                by u/{post.author}
              </div>
            )}
        </header>

        {/* Gemini TL;DR Card - Renders below metadata */}
        <GeminiTldrCard
          isVisible={isTldrCardVisible}
          isLoading={isTldrLoading}
          error={tldrError}
          tldrContent={tldrContent}
          onDismiss={handleDismissTldr}
        />

        {/* Post Content */}
        <PostContent post={displayPost} />
      </article>

      {/* Comments Section */}
      <section
        id="comments-section"
        aria-labelledby="comments-heading"
        className="pt-12 border-t border-gray-200 dark:border-gray-700"
      >
        <h2
          id="comments-heading"
          className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-10"
        >
          Comments ({displayPost.num_comments.toLocaleString()})
        </h2>

        {/* Loading indicator for initial comments load */}
        {showCommentsLoading && comments.length === 0 && (
          <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 text-sm my-6">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Loading comments...</span>
          </div>
        )}

        {/* Error message specifically for comments */}
        {commentsError &&
          !loadingMoreId && ( // Show general comment error if not loading 'more'
            <div
              className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 px-3 py-2 rounded text-sm mb-4"
              role="alert"
            >
              {commentsError}
            </div>
          )}
        {/* Error message shown near a 'more' button if that specific load failed */}
        {commentsError && loadingMoreId && (
          <div
            className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 px-3 py-2 rounded text-sm my-4"
            role="alert"
          >
            Could not load more comments here. {commentsError}
          </div>
        )}

        {/* Render Comment Tree if comments are loaded or partially loaded */}
        {/* Add opacity transition for smoother loading appearance */}
        <div
          className={`transition-opacity duration-300 ${
            isCommentsPending ? "opacity-50" : "opacity-100"
          }`}
        >
          {comments.length > 0 && (
            <CommentTree
              comments={comments}
              postAuthor={displayPost.author}
              onLoadMore={handleLoadMoreComments}
              loadingMoreId={loadingMoreId}
              indentSize={20} // Increased indent size
            />
          )}
        </div>

        {/* No comments message */}
        {!showCommentsLoading &&
          commentsFetchAttempted &&
          !commentsError &&
          comments.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No comments yet.
            </p>
          )}
      </section>
    </div>
  );
};

export default PostPage;
